import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, MapPin, Users, Video, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ShareButton } from "@/components/site/ShareArticle";
import { Spinner } from "@/components/site/ProtectedRoute";

const img = (src) => (src?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src);
const local = (iso) => new Date(iso).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });

function BookForm({ c }) {
  const [f, setF] = useState({ name: "", email: "", whatsapp: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/classes/${c.slug}/book`, f); setDone(data); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="rounded-2xl bg-[#F5ECD7] p-6 text-center" data-testid="class-book-success">
      <CheckCircle2 className="h-10 w-10 text-[#1A9AA8] mx-auto" />
      <p className="mt-3 font-bold text-[#0B2A4A]">You're booked! A confirmation email is on its way.</p>
      {done.video_link && <a href={done.video_link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block font-semibold text-[#147C88] underline" data-testid="class-video-link">Your join link</a>}
    </div>
  );
  if (c.seats_left === 0) return <p className="rounded-2xl bg-slate-100 p-6 text-center font-semibold text-slate-600" data-testid="class-full">This class is full.</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="flex flex-col gap-3" data-testid="class-book-form">
      <Input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="class-book-name" />
      <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="class-book-email" />
      <Input required type="tel" pattern="[0-9+\s\(\)\.\-]{7,}" title="WhatsApp number with country code" placeholder="WhatsApp number (with country code)" value={f.whatsapp} onChange={set("whatsapp")} data-testid="class-book-whatsapp" />
      <button disabled={busy} className="rounded-xl bg-[#F08A3C] text-white px-5 py-3 font-semibold hover:bg-[#D96F28] disabled:opacity-60" data-testid="class-book-submit">{busy ? "Booking..." : "Book my free seat"}</button>
    </form>
  );
}

function Facts({ c }) {
  return (
    <ul className="flex flex-col gap-2 text-sm text-slate-700">
      <li className="flex gap-2"><CalendarDays className="h-4 w-4 text-[#F08A3C] mt-0.5" /> <span data-testid="class-when">{local(c.starts_at)} (your time) · {c.when}</span></li>
      <li className="flex gap-2">{c.mode === "online" ? <Video className="h-4 w-4 text-[#F08A3C] mt-0.5" /> : <MapPin className="h-4 w-4 text-[#F08A3C] mt-0.5" />} {c.mode === "online" ? "Online — link shown after booking" : c.location}</li>
      <li className="flex gap-2"><Users className="h-4 w-4 text-[#F08A3C] mt-0.5" /> <span data-testid="class-seats-left">{c.seats_left} of {c.seats} seats left · free</span></li>
    </ul>
  );
}

export default function ClassPage() {
  const { slug } = useParams();
  const [c, setC] = useState(undefined);
  useEffect(() => { api.get(`/classes/${slug}`).then((r) => setC(r.data)).catch(() => setC(null)); }, [slug]);
  useSeo({ title: c ? `${c.title} — Free class` : "Free class", description: c?.description?.slice(0, 155) || "A free class with Greg Miceli, Digital Nomad Nicaragua.", path: `/classes/${slug}`, image: c?.image ? img(c.image) : undefined });
  if (c === undefined) return <Spinner />;
  if (c === null) return <p className="text-center py-24 text-slate-600" data-testid="class-not-found">This class isn't available. <Link to="/" className="underline">Go home</Link></p>;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start" data-testid="class-page">
      {c.image ? <img src={img(c.image)} alt={c.image_alt || c.title} className="w-full max-h-[640px] object-contain rounded-3xl bg-[#0B2A4A]/5" data-testid="class-image" /> : <div className="aspect-video rounded-3xl bg-[#0B2A4A]" />}
      <div className="flex flex-col gap-5">
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">{c.mode === "online" ? "FREE ONLINE CLASS WITH GREG" : "FREE CLASS AT Esc DIGITAL NOMAD VILLAGE"}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2A4A]" data-testid="class-title">{c.title}</h1>
        <Facts c={c} />
        {c.description && <p className="text-slate-700 leading-relaxed whitespace-pre-line">{c.description}</p>}
        <BookForm c={c} />
        <ShareButton link={window.location.href} title={c.title} label="Share this class" testId="class-share-btn" />
      </div>
    </section>
  );
}

export function UpcomingClass() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/classes").then((r) => setC(r.data[0] || null)).catch(() => {}); }, []);
  if (!c) return null;
  return (
    <section className="bg-[#0B2A4A] text-[#F5ECD7]" data-testid="upcoming-class">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid md:grid-cols-[1fr_1.4fr] gap-8 items-center">
        {c.image ? <img src={img(c.image)} alt={c.image_alt || c.title} className="w-full max-h-80 object-contain rounded-2xl" /> : <div />}
        <div>
          <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C]">{c.mode === "online" ? "NEXT FREE ONLINE CLASS" : "NEXT FREE CLASS · HOSTED AT Esc DIGITAL NOMAD VILLAGE"}</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-white">{c.title}</h2>
          <p className="mt-3 text-sm">{local(c.starts_at)} (your time) · {c.when} · {c.mode === "online" ? "Online" : c.location} · {c.seats_left} seats left</p>
          <Link to={`/classes/${c.slug}`} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold hover:bg-[#D96F28]" data-testid="upcoming-class-btn">Book a free seat <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>
  );
}
